import React, { useContext, useState, useMemo } from 'react';
import ToastMessage, { ToastStyle } from './ToastMessage';

// Toast state type
type ToastState = { show: boolean; type: ToastStyle | ''; header: string; message: string };

// Separate contexts for state and dispatch to optimize re-renders
const ToastStateContext = React.createContext<{ toast: ToastState }>({
    toast: { show: false, type: '', header: '', message: '' },
});

const ToastDispatchContext = React.createContext<{
    setToast: React.Dispatch<React.SetStateAction<ToastState>>;
}>({
    setToast: () => { },
});

// Hook for triggering toasts (does not trigger re-render on toast state changes)
export function useToast() {
    return useContext(ToastDispatchContext);
}

// Hook for reading toast state
export function useToastState() {
    return useContext(ToastStateContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toast, setToast] = useState<ToastState>({
        show: false,
        type: '',
        header: '',
        message: '',
    });

    // Memoize dispatch value to maintain a stable reference
    const dispatchValue = useMemo(() => ({ setToast }), [setToast]);

    return (
        <ToastDispatchContext.Provider value={dispatchValue}>
            <ToastStateContext.Provider value={{ toast }}>
                {children}
                <ToastMessage toast={toast} setToast={setToast} />
            </ToastStateContext.Provider>
        </ToastDispatchContext.Provider>
    );
}
